/* eslint-disable max-len */
import { animated, config, useSpring, useTrail } from '@react-spring/web'
import { useScroll } from '@use-gesture/react'
import React, { ErrorInfo, PropsWithChildren, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { CartDrawer } from '~/components/Cart'
import { CountrySelector } from '~/components/Locale'
import { Portal } from '~/components/utils'
import { cn, imageUrl, isHome } from '~/utils'
import { AnnouncementBar, AnnouncementBarHeight, showHeader } from './AnnouncementBar'
import { MenuButton } from './MenuButton'

const HeaderElem: React.FC = () => {
	const { links, settings } = window.ShopifyHeader

	const ref = useRef<HTMLDivElement>(null)
	const baseY = showHeader(settings) ? AnnouncementBarHeight : 0
	const headerHeight = (ref.current?.offsetHeight || 72) + baseY

	const [navOpen, setNavOpen] = useState(false)
	const [isFixed, setFixed] = useState(false)
	const [styles, api] = useSpring(() => ({
		y: 0,
		config: { tension: 120, friction: 25, bounce: 0 },
		onRest: {
			y: y => y.value == -headerHeight && setFixed(false)
		}
	}))
	const [trail, trailAPI] = useTrail(links.length, () => ({
		opacity: 0,
		transform: 'translateY(100%)',
		config: config.stiff
	}))

	useEffect(() => {
		if (navOpen) {
			document.body.classList.add('navigation-open')
			trailAPI.start({ opacity: 1, transform: 'translateY(0%)' })
		} else {
			document.body.classList.remove('navigation-open')
			trailAPI.set({ opacity: 0, transform: 'translateY(100%)' })
		}
		return () => {
			document.body.classList.remove('navigation-open')
		}
	}, [navOpen, trailAPI])

	useLayoutEffect(() => {
		if (isFixed) {
			document.body.classList.add('header--fixed')
		} else {
			document.body.classList.remove('header--fixed')
		}
		return () => {
			document.body.classList.remove('header--fixed')
		}
	}, [isFixed])

	useScroll(
		({ direction: [, direction], values: [, y] }) => {
			if (y <= baseY) {
				setFixed(false)
				api.start({ y: 0 })
			} else if (y >= headerHeight * 4) {
				if (direction == -1) {
					setFixed(true)
					api.start({ y: 0 })
				} else if (direction == 1) {
					api.start({ y: -headerHeight })
				}
			}
		},
		{ target: window, threshold: 5 }
	)
	const isTransparent = settings.enable_transparent && isHome() && !(isFixed || navOpen)
	return (
		<>
			<div className="invisible" style={{ height: isHome() ? baseY : headerHeight }} />
			<animated.header
				className={cn(
					'absolute inset-x-0 top-0 z-30 transition-colors',
					(isTransparent || navOpen) && !isFixed ? 'bg-transparent' : 'bg-white',
					{ fixed: isFixed }
				)}
				style={{
					...styles,
					color: isTransparent ? settings.transparent_text_color : undefined
				}}
			>
				<AnnouncementBar settings={settings} isFixed={isFixed} />
				<div
					className="mx-auto flex max-w-[92vw] items-center justify-between py-4 xl:max-w-[160vh] xl:px-16"
					ref={ref}
				>
					{settings.show_nav && (
						<nav className="w-[50px]">
							<MenuButton onClick={_ => setNavOpen(!navOpen)} aria-expanded={navOpen} />
							<Portal open={navOpen}>
								<animated.div
									className="portal z-10 bg-white"
									style={{
										opacity: trail[0].opacity.to(o => o + 0.2),
										paddingTop: headerHeight
									}}
								>
									<ul
										className="navigation__links text-center"
										style={{ marginTop: ref.current?.getBoundingClientRect().y }}
									>
										{trail.map((trailStyles, i) => {
											const link = links[i]
											return (
												<animated.li
													key={link.title}
													style={trailStyles}
													className={cn('navigation__link', {
														'navigation__link--active': link.active
													})}
												>
													<a href={link.url} aria-current={link.active ? 'page' : undefined}>
														{link.title}
													</a>
												</animated.li>
											)
										})}
									</ul>
								</animated.div>
							</Portal>
						</nav>
					)}
					<div itemType="http://schema.org/Organization" className="contents">
						<a href="/" itemProp="url" className="contents">
							<img
								height={settings.logo_max_height}
								src={imageUrl(
									isTransparent ? settings.transparent_logo ?? settings.logo : settings.logo,
									settings.logo_max_height * 2
								)}
								alt="Auk"
								itemProp="logo"
							/>
						</a>
					</div>

					<div className="flex w-[50px] flex-row-reverse items-center gap-x-1">
						<CartDrawer settings={settings} />
						<CountrySelector headerOpen={isFixed} />
					</div>
				</div>
			</animated.header>
		</>
	)
}

class HeaderErrorBoundary extends React.Component<PropsWithChildren> {
	state = {
		hasError: false,
		error: null,
		info: null
	}

	static getDerivedStateFromError = (_: Error) => ({ hasError: true })

	componentDidCatch = (error: Error, info: ErrorInfo) => {
		console.warn(error, info)
		this.setState({ error, info })
	}

	render() {
		const { hasError } = this.state
		return hasError ? null : this.props.children
	}
}

export const Header = () => (
	<HeaderErrorBoundary>
		<HeaderElem />
	</HeaderErrorBoundary>
)
